import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Clapperboard, Users, Star, Tv, Newspaper, Megaphone, Settings, Inbox, LogOut, ExternalLink, Trash2, CalendarDays, Store, Mail, Download, ShoppingBag, Package, ChefHat,
} from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { EntityManager } from "./EntityManager";
import { ImageUpload } from "./ImageUpload";

const FIELDS = {
  episodes: [
    { name: "title", label: "Title", required: true },
    { name: "kind", label: "Type", type: "select", options: ["Full Episode", "Interview Clip", "Cooking Segment", "Guest Moment", "Behind the Scenes"] },
    { name: "description", label: "Description", type: "textarea" },
    { name: "video_url", label: "Video URL (YouTube or Vimeo link)" },
    { name: "thumbnail", label: "Thumbnail", type: "image" },
    { name: "featured", label: "Featured on homepage", type: "boolean" },
    { name: "published_at", label: "Date (e.g. 2026-06-01)" },
  ],
  cast: [
    { name: "name", label: "Name", required: true },
    { name: "role", label: "Role on the show" },
    { name: "bio", label: "Bio", type: "textarea" },
    { name: "photo", label: "Headshot", type: "image" },
    { name: "instagram", label: "Instagram URL" },
    { name: "twitter", label: "X / Twitter URL" },
    { name: "order", label: "Display order", type: "number" },
    { name: "is_founder", label: "Founder / leadership", type: "boolean" },
  ],
  guests: [
    { name: "name", label: "Guest name", required: true },
    { name: "descriptor", label: "Short descriptor (e.g. Award-winning artist)" },
    { name: "category", label: "Category", type: "select", options: ["Music", "Entertainment", "Sports", "Media", "Leaders & Public Figures", "Culinary", "Community", "Business", "Other"] },
    { name: "bio", label: "Bio / appearance notes", type: "textarea" },
    { name: "photo", label: "Photo", type: "image" },
    { name: "focus", label: "Photo focus (object-position, e.g. 'center 30%')", type: "text" },
    { name: "appearance_link", label: "Appearance / highlight clip (video file or URL)" },
    { name: "episode_link", label: "Full episode URL (optional)" },
    { name: "featured", label: "Featured on homepage", type: "boolean" },
    { name: "group", label: "Section", type: "select", options: ["Show Guest", "VIP & Special Guest"] },
    { name: "designation", label: "VIP designation (e.g. POOLSIDE VIP, Special Guest)" },
    { name: "appearance_note", label: "Appearance / event note (e.g. Welcomed at the Café)" },
    { name: "order", label: "Display order", type: "number" },
  ],
  platforms: [
    { name: "name", label: "Platform / network name", required: true },
    { name: "detail", label: "Airtimes / details" },
    { name: "link", label: "Link" },
    { name: "logo", label: "Logo", type: "image" },
    { name: "order", label: "Display order", type: "number" },
  ],
  press: [
    { name: "title", label: "Title", required: true },
    { name: "kind", label: "Type", type: "select", options: ["TV Appearance", "Press Coverage", "Interview", "Milestone"] },
    { name: "outlet", label: "Outlet / source" },
    { name: "date", label: "Date" },
    { name: "excerpt", label: "Excerpt", type: "textarea" },
    { name: "link", label: "Link" },
    { name: "image", label: "Image", type: "image" },
  ],
  announcements: [
    { name: "text", label: "Announcement text", type: "textarea", required: true },
    { name: "link", label: "Link (optional)" },
    { name: "active", label: "Active (shows in top bar)", type: "boolean" },
  ],
  events: [
    { name: "title", label: "Event / experience title", required: true },
    { name: "kind", label: "Type", type: "select", options: ["Celebrity Brunch", "Private Event", "Special Event", "Community Experience", "Signature Experience", "Other"] },
    { name: "date", label: "Date (e.g. Saturday, October 17, 2026)" },
    { name: "event_date", label: "Exact date, YYYY-MM-DD (auto-moves to Past Events after this date)" },
    { name: "time", label: "Time (e.g. 1:00 PM)" },
    { name: "venue", label: "Venue (e.g. Poolside Cooking Café · Virginia Beach, VA)" },
    { name: "description", label: "Description", type: "textarea" },
    { name: "image", label: "Photo / flyer", type: "image" },
    { name: "link", label: "Ticket / event link (optional)" },
    { name: "guest_link", label: "Guest profile link (shows VIEW GUEST on past events)" },
    { name: "order", label: "Display order", type: "number" },
  ],
  cafe: [
    { name: "title", label: "Photo title", required: true },
    { name: "image", label: "Photo", type: "image" },
    { name: "order", label: "Display order", type: "number" },
  ],
  products: [
    { name: "name", label: "Product name", required: true },
    { name: "category", label: "Category", type: "select", options: ["Merch", "Cookbook", "Gift Cards", "Experiences"] },
    { name: "kind", label: "Product type", type: "select", options: ["Physical Product", "Digital Product", "Gift Card", "Experience"] },
    { name: "status", label: "Status (COMING SOON hides price & cart)", type: "select", options: ["DRAFT", "COMING SOON", "AVAILABLE", "SOLD OUT"] },
    { name: "description", label: "Description", type: "textarea" },
    { name: "price", label: "Price in USD (0 = pricing TBA)", type: "number" },
    { name: "variants", label: "Variant labels (e.g. S,M,L,XL)", type: "text" },
    { name: "variant_prices", label: "Variant prices (e.g. $25=25,$50=50)", type: "text" },
    { name: "inventory", label: "Inventory (blank = unlimited)", type: "number" },
    { name: "allow_backorder", label: "Allow backorders when sold out", type: "boolean" },
    { name: "featured", label: "Featured", type: "boolean" },
    { name: "published", label: "Published (visible in shop)", type: "boolean" },
    { name: "image", label: "Product photo", type: "image" },
    { name: "image_2", label: "Hover / secondary photo (optional)", type: "image" },
    { name: "external_link", label: "Purchase / booking link (e.g. Amazon)" },
    { name: "external_link_2", label: "Second purchase link (e.g. Barnes & Noble)" },
    { name: "experience_date", label: "Experience date (experiences only)" },
    { name: "experience_location", label: "Experience location (experiences only)" },
    { name: "order", label: "Display order", type: "number" },
  ],
};

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "episodes", label: "Episodes & Clips", icon: Clapperboard },
  { key: "cast", label: "Cast", icon: Users },
  { key: "guests", label: "Guests", icon: Star },
  { key: "platforms", label: "Where to Watch", icon: Tv },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "cafe", label: "Café Photos", icon: Store },
  { key: "press", label: "Press & Media", icon: Newspaper },
  { key: "announcements", label: "Announcements", icon: Megaphone },
  { key: "settings", label: "Homepage & Settings", icon: Settings },
  { key: "inquiries", label: "Inquiries", icon: Inbox },
  { key: "casting", label: "Be on the Show", icon: ChefHat },
  { key: "subscribers", label: "Subscribers", icon: Mail },
  { key: "shop", label: "Shop Products", icon: ShoppingBag },
  { key: "orders", label: "Orders", icon: Package },
];

function Overview({ go }) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    ["episodes", "cast", "guests", "platforms", "press", "announcements"].forEach((c) => {
      api.get(`/content/${c}`).then((r) => setCounts((p) => ({ ...p, [c]: r.data.length }))).catch(() => {});
    });
    api.get("/admin/inquiries").then((r) => setCounts((p) => ({ ...p, inquiries: r.data.length }))).catch(() => {});
  }, []);

  const cards = [
    { key: "episodes", label: "Episodes & clips" },
    { key: "cast", label: "Cast members" },
    { key: "guests", label: "Guests" },
    { key: "platforms", label: "Watch platforms" },
    { key: "press", label: "Press items" },
    { key: "inquiries", label: "Inquiries" },
  ];

  return (
    <div data-testid="admin-overview">
      <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Welcome back.</h2>
      <p className="text-sm text-slate-500 mb-8">Everything on the website is editable from this dashboard — no developer needed.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => go(c.key)}
            className="bg-white border border-slate-200 p-6 text-left hover:border-pool transition-colors"
            data-testid={`overview-card-${c.key}`}
          >
            <p className="font-display font-black text-4xl text-slate-900">{counts[c.key] ?? "—"}</p>
            <p className="mt-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-slate-500">{c.label}</p>
          </button>
        ))}
      </div>
      <div className="mt-8 bg-pool-light/40 border border-pool/30 p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold">Tip:</span> images can be uploaded directly in any form — or paste an image URL.
          Episodes accept YouTube or Vimeo links; no video files needed.
        </p>
      </div>
    </div>
  );
}

function SettingsForm() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  if (!form) return <p className="text-sm text-slate-400 py-10">Loading…</p>;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    }
    setBusy(false);
  };

  const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-pool";

  return (
    <form onSubmit={save} data-testid="admin-settings-form" className="max-w-2xl">
      <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Homepage & Settings</h2>
      <p className="text-sm text-slate-500 mb-8">Control the homepage hero and site-wide social links.</p>
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hero headline</label>
          <input value={form.hero_headline || ""} onChange={(e) => setForm({ ...form, hero_headline: e.target.value })} className={inputCls} data-testid="settings-hero-headline" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hero supporting copy</label>
          <textarea rows={3} value={form.hero_subcopy || ""} onChange={(e) => setForm({ ...form, hero_subcopy: e.target.value })} className={inputCls} data-testid="settings-hero-subcopy" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hero eyebrow</label>
          <input value={form.hero_eyebrow || ""} onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })} className={inputCls} data-testid="settings-hero-eyebrow" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Hero image</label>
          <ImageUpload value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testId="settings-hero-image" />
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400 pt-4">Cookbook</p>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Cookbook cover</label>
          <ImageUpload value={form.cookbook_cover} onChange={(v) => setForm({ ...form, cookbook_cover: v })} testId="settings-cookbook-cover" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Amazon link</label>
            <input value={form.cookbook_amazon || ""} onChange={(e) => setForm({ ...form, cookbook_amazon: e.target.value })} className={inputCls} data-testid="settings-cookbook-amazon" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Barnes &amp; Noble link</label>
            <input value={form.cookbook_bn || ""} onChange={(e) => setForm({ ...form, cookbook_bn: e.target.value })} className={inputCls} data-testid="settings-cookbook-bn" />
          </div>
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400 pt-4">Your Channel Network</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Website link</label>
            <input value={form.ycn_website_link || ""} onChange={(e) => setForm({ ...form, ycn_website_link: e.target.value })} className={inputCls} data-testid="settings-ycn-website" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">App Store link</label>
            <input value={form.ycn_appstore_link || ""} onChange={(e) => setForm({ ...form, ycn_appstore_link: e.target.value })} className={inputCls} data-testid="settings-ycn-appstore" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Google Play link</label>
            <input value={form.ycn_googleplay_link || ""} onChange={(e) => setForm({ ...form, ycn_googleplay_link: e.target.value })} className={inputCls} data-testid="settings-ycn-googleplay" />
          </div>
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400 pt-4">Café</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Café address</label>
            <input value={form.cafe_address || ""} onChange={(e) => setForm({ ...form, cafe_address: e.target.value })} className={inputCls} data-testid="settings-cafe-address" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Café phone</label>
            <input value={form.cafe_phone || ""} onChange={(e) => setForm({ ...form, cafe_phone: e.target.value })} className={inputCls} data-testid="settings-cafe-phone" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Café hours</label>
            <input value={form.cafe_hours || ""} onChange={(e) => setForm({ ...form, cafe_hours: e.target.value })} className={inputCls} data-testid="settings-cafe-hours" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Menu link</label>
            <input value={form.cafe_menu_link || ""} onChange={(e) => setForm({ ...form, cafe_menu_link: e.target.value })} className={inputCls} data-testid="settings-cafe-menu-link" />
          </div>
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-400 pt-4">Social links</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {["instagram", "youtube", "facebook", "tiktok"].map((k) => (
            <div key={k}>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{k} URL</label>
              <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`settings-${k}`} />
            </div>
          ))}
        </div>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="mt-8 bg-slate-950 hover:bg-pool disabled:opacity-60 text-white text-[11px] tracking-wider uppercase font-semibold px-7 py-3.5 transition-colors"
        data-testid="settings-save"
      >
        {busy ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}

function InquiriesList() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load inquiries"));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div data-testid="admin-inquiries">
      <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Inquiries</h2>
      <p className="text-sm text-slate-500 mb-8">Messages from the Contact / Be On The Show form.</p>
      {items === null ? (
        <p className="text-sm text-slate-400 py-10">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 border border-dashed border-slate-300 text-center" data-testid="inquiries-empty">
          No inquiries yet.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {items.map((i) => (
            <div key={i.id} className="px-5 sm:px-6 py-5 flex items-start gap-4" data-testid={`inquiry-${i.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="bg-pool-light text-pool-deep text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1">
                    {i.inquiry_type || "General"}
                  </span>
                  <p className="font-semibold text-sm text-slate-900">{i.name}</p>
                  <a href={`mailto:${i.email}`} className="text-xs text-pool hover:underline">{i.email}</a>
                </div>
                {i.detail && <p className="text-xs text-slate-500 mt-2">{i.detail}</p>}
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{i.message}</p>
                {i.created_at && (
                  <p className="text-[11px] text-slate-400 mt-2">{new Date(i.created_at).toLocaleString()}</p>
                )}
              </div>
              <button onClick={() => remove(i.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors" aria-label="Delete" data-testid={`inquiry-delete-${i.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubscribersList() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setData(r.data)).catch(() => toast.error("Failed to load subscribers"));
  }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "poolside-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Subscribers</h2>
          <p className="text-sm text-slate-500">Everyone who joined the Stay Poolside list.</p>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 bg-slate-950 hover:bg-pool text-white text-[11px] tracking-wider uppercase font-semibold px-5 py-3 transition-colors"
          data-testid="subscribers-export"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="bg-white border border-slate-200 p-6 mb-6 inline-block" data-testid="subscribers-total">
        <p className="font-display font-black text-4xl text-slate-900">{data?.total ?? "—"}</p>
        <p className="mt-1 text-[11px] tracking-[0.18em] uppercase font-semibold text-slate-500">Total subscribers</p>
      </div>
      {data === null ? (
        <p className="text-sm text-slate-400 py-10">Loading…</p>
      ) : data.subscribers.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 border border-dashed border-slate-300 text-center" data-testid="subscribers-empty">
          No subscribers yet. The Stay Poolside form appears above the footer on every public page.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] tracking-[0.15em] uppercase text-slate-500">
                <th className="px-5 py-3.5 font-semibold">Email</th>
                <th className="px-5 py-3.5 font-semibold">Signup date</th>
                <th className="px-5 py-3.5 font-semibold">Source</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.subscribers.map((s) => (
                <tr key={s.id} data-testid={`subscriber-${s.id}`}>
                  <td className="px-5 py-3.5 text-slate-900 font-medium">{s.email}</td>
                  <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3.5 text-slate-500">{s.source || "website"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1 ${s.status === "subscribed" ? "bg-pool-light text-pool-deep" : "bg-slate-100 text-slate-500"}`}>
                      {s.status || "subscribed"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const ORDER_STATUSES = ["NEW", "PAID", "PROCESSING", "READY FOR PICKUP", "SHIPPED", "COMPLETED", "CANCELLED", "REFUNDED"];

function OrdersList() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load orders"));
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success("Order updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div data-testid="admin-orders">
      <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Orders</h2>
      <p className="text-sm text-slate-500 mb-8">Shop orders and their fulfillment status.</p>
      {items === null ? (
        <p className="text-sm text-slate-400 py-10">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 border border-dashed border-slate-300 text-center" data-testid="orders-empty">
          No orders yet.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {items.map((o) => (
            <div key={o.id} className="px-5 sm:px-6 py-5" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="font-display font-bold text-sm text-slate-900" data-testid={`order-number-${o.id}`}>{o.order_number}</p>
                <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1 ${o.payment_status === "paid" ? "bg-pool-light text-pool-deep" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                  {o.payment_status === "paid" ? "Paid" : o.payment_status}
                </span>
                <p className="text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</p>
                <p className="text-sm font-bold text-slate-900 ml-auto">{money(o.subtotal_cents)}</p>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {o.customer?.name} · {o.customer?.email}{o.customer?.phone ? ` · ${o.customer.phone}` : ""} · {o.fulfillment === "shipping" ? "Shipping" : "Café pickup"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {(o.items || []).map((i) => `${i.name}${i.variant ? ` (${i.variant})` : ""} ×${i.quantity}`).join(" · ")}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Status</label>
                <select
                  value={o.status || "NEW"}
                  onChange={(e) => setStatus(o.id, e.target.value)}
                  className="border border-slate-300 text-xs px-3 py-2 bg-white focus:outline-none focus:border-pool"
                  data-testid={`order-status-${o.id}`}
                >
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CastingList() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/casting").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load applications"));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admin/casting/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div data-testid="admin-casting">
      <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Be on the Show</h2>
      <p className="text-sm text-slate-500 mb-8">Casting applications from the Be on the Show page.</p>
      {items === null ? (
        <p className="text-sm text-slate-400 py-10">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 border border-dashed border-slate-300 text-center" data-testid="casting-empty">
          No applications yet.
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 p-5 sm:p-6" data-testid={`casting-${a.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="font-display font-bold text-sm text-slate-900">{a.name}</p>
                <span className="bg-pool-light text-pool-deep text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1">{a.appear_as || "Applicant"}</span>
                <p className="text-xs text-slate-400">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</p>
                <button onClick={() => remove(a.id)} className="ml-auto text-slate-300 hover:text-red-500" aria-label="Delete" data-testid={`casting-delete-${a.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {a.email}{a.phone ? ` · ${a.phone}` : ""}{a.city_state ? ` · ${a.city_state}` : ""}
              </p>
              {(a.instagram || a.website || a.media_link) && (
                <p className="text-xs text-pool mt-1 space-x-3">
                  {a.instagram && <span>IG: {a.instagram}</span>}
                  {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="hover:underline">Website</a>}
                  {a.media_link && <a href={a.media_link} target="_blank" rel="noreferrer" className="hover:underline">Media link</a>}
                </p>
              )}
              {a.about && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{a.about}</p>}
              {a.topic && <p className="text-xs text-slate-500 mt-2 italic">Showcase: {a.topic}</p>}
              {a.photo_url && (
                <a href={imgSrc(a.photo_url)} target="_blank" rel="noreferrer" className="inline-block mt-3" data-testid={`casting-photo-${a.id}`}>
                  <img src={imgSrc(a.photo_url)} alt={`${a.name} headshot`} className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [section, setSection] = useState("overview");
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-400 text-xs tracking-[0.25em] uppercase">
        Loading…
      </div>
    );
  }

  const logout = async () => {
    localStorage.removeItem("pc_token");
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-dashboard">
      <aside className="w-60 shrink-0 bg-slate-950 text-white min-h-screen p-6 hidden md:flex flex-col sticky top-0 h-screen">
        <img src="/logo.png" alt="Poolside Cooking" className="h-12 w-auto self-start mb-8" />
        <nav className="flex-1 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                section === s.key ? "bg-pool text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid={`admin-nav-${s.key}`}
            >
              <s.icon className="h-4 w-4 shrink-0" /> {s.label}
            </button>
          ))}
        </nav>
        <div className="pt-6 border-t border-slate-800 space-y-1">
          <a href="/" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="admin-view-site">
            <ExternalLink className="h-4 w-4" /> View site
          </a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden bg-slate-950 px-4 py-3 flex items-center gap-3 overflow-x-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`whitespace-nowrap text-[11px] tracking-wider uppercase font-semibold px-3 py-2 transition-colors ${
                section === s.key ? "bg-pool text-white" : "text-slate-400"
              }`}
              data-testid={`admin-nav-mobile-${s.key}`}
            >
              {s.label}
            </button>
          ))}
          <button onClick={logout} className="whitespace-nowrap text-[11px] tracking-wider uppercase font-semibold px-3 py-2 text-slate-400" data-testid="admin-logout-mobile">
            Sign out
          </button>
        </div>

        <main className="p-5 sm:p-10 max-w-5xl">
          {section === "overview" && <Overview go={setSection} />}
          {section === "episodes" && (
            <EntityManager collection="episodes" title="Episodes & Clips" subtitle="Paste a YouTube or Vimeo link — no video upload needed." fields={FIELDS.episodes} />
          )}
          {section === "cast" && (
            <EntityManager collection="cast" title="Cast" subtitle="Headshots, roles, bios, and social links." fields={FIELDS.cast} />
          )}
          {section === "guests" && (
            <EntityManager collection="guests" title="Guests" subtitle="The guest archive — add new guests as episodes air." fields={FIELDS.guests} />
          )}
          {section === "platforms" && (
            <EntityManager collection="platforms" title="Where to Watch" subtitle="Networks, streaming platforms, airtimes, and links." fields={FIELDS.platforms} />
          )}
          {section === "events" && (
            <EntityManager collection="events" title="Events & Experiences" subtitle="Celebrity brunches, private events, and special gatherings at the Café." fields={FIELDS.events} />
          )}
          {section === "cafe" && (
            <EntityManager collection="cafe" title="Café Photos" subtitle="Photography shown on the Café page." fields={FIELDS.cafe} />
          )}
          {section === "press" && (
            <EntityManager collection="press" title="Press & Media" subtitle="TV appearances, coverage, interviews, and milestones." fields={FIELDS.press} />
          )}
          {section === "announcements" && (
            <EntityManager collection="announcements" title="Announcements" subtitle="Shown in the bar at the top of the site. Only the first active one displays." fields={FIELDS.announcements} />
          )}
          {section === "settings" && <SettingsForm />}
          {section === "inquiries" && <InquiriesList />}
          {section === "casting" && <CastingList />}
          {section === "subscribers" && <SubscribersList />}
          {section === "shop" && (
            <EntityManager collection="products" title="Shop Products" subtitle="Set Status to COMING SOON to preview without pricing, AVAILABLE to start selling, DRAFT to hide." fields={FIELDS.products} />
          )}
          {section === "orders" && <OrdersList />}
        </main>
      </div>
    </div>
  );
}
